/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 27 2023
 **/

import { useEffect, useMemo, useState } from "react";

import { MinimizeChatItemType } from "@/frameworks/core/hooks";

import * as C from "./constant";

export default function useScrollMinimizeChatList(
	totalChatList: Record<string, MinimizeChatItemType>,
	scrollWrapper: HTMLElement | null
) {
	const chatList = useMemo(() => {
		return Object.keys(totalChatList);
	}, [totalChatList]);
	const [showAvatarLength, setShowAvatarLength] = useState(chatList.length);
	const [page, setPage] = useState(0);
	const [lastPage, setLastPage] = useState(Math.ceil(chatList.length / showAvatarLength));

	const totalHeight = useMemo(() => {
		let result =
			C.HEADER_HEIGHT +
			C.ARROW_ICON_HEIGHT +
			C.GAP +
			showAvatarLength * C.SINGLE_AVATAR_HEIGHT +
			C.MORE_ICON_HEIGHT +
			C.GAP +
			C.ARROW_ICON_HEIGHT;

		if (chatList.length < 2) result -= C.MORE_ICON_HEIGHT;
		return result;
	}, [showAvatarLength, page]);

	const readjustPage = (newLastPage: number) => {
		if (newLastPage < 0) {
			if (page !== 0) setPage(0);
			setLastPage(0);
			return;
		}
		if (page > newLastPage) {
			setPage(newLastPage);
		}
		if (lastPage !== newLastPage) {
			setLastPage(newLastPage);
		}
	};

	const handleChangeShowAvatarLength = (ev?: Event) => {
		const target = ev ? (ev.target as Window) : window;
		const { clientHeight } = target.document.documentElement;

		setShowAvatarLength((prev) => {
			let result = prev;

			if (totalHeight > clientHeight) {
				const calculatedResult = Math.max(
					1,
					prev - Math.ceil((totalHeight - clientHeight) / C.SINGLE_AVATAR_HEIGHT)
				);
				if (calculatedResult !== prev) result = calculatedResult;
			} else if (clientHeight >= totalHeight + C.SINGLE_AVATAR_HEIGHT) {
				const calculatedResult = prev + Math.ceil((clientHeight - totalHeight) / C.SINGLE_AVATAR_HEIGHT);
				if (calculatedResult !== prev) result = calculatedResult;
			}

			if (result !== prev) {
				readjustPage(Math.ceil(chatList.length / result) - 1);
			}
			return result;
		});
	};

	const handleWheel = (ev: WheelEvent) => {
		if (ev.deltaY > 0) {
			if (page === 0) return;
			setPage(page - 1);
		} else {
			if (page === lastPage) return;
			setPage(page + 1);
		}
	};

	const handleClickArrow = (ev: React.MouseEvent<HTMLButtonElement>) => {
		const target = ev.currentTarget as HTMLButtonElement;
		if (target.id === "up" && page < lastPage) {
			setPage((prev) => Math.min(prev + 1, lastPage));
		} else if (target.id === "down" && page > 0) {
			setPage((prev) => Math.max(prev - 1, 0));
		}
	};

	useEffect(() => {
		const initializeShowAvatarLength = () => {
			const clientHeight = window.document.documentElement.clientHeight;
			const initialResult = Math.floor(
				(clientHeight - (C.HEADER_HEIGHT + C.ARROW_ICON_HEIGHT * 2 + C.GAP * 2 + C.MORE_ICON_HEIGHT)) /
					C.SINGLE_AVATAR_HEIGHT
			);
			setShowAvatarLength(initialResult);
			setLastPage(Math.ceil(chatList.length / initialResult) - 1);
		};

		initializeShowAvatarLength();
	}, [chatList]);

	useEffect(() => {
		window.addEventListener("resize", handleChangeShowAvatarLength);
		return () => window.removeEventListener("resize", handleChangeShowAvatarLength);
	}, [totalHeight, chatList]);

	useEffect(() => {
		if (!scrollWrapper) return;
		scrollWrapper.addEventListener("wheel", handleWheel, { passive: true });
		return () => {
			if (!scrollWrapper) return;
			scrollWrapper.removeEventListener("wheel", handleWheel);
		};
	}, [page, lastPage]);

	return {
		page,
		lastPage,
		showAvatarLength,
		handleClickArrow
	};
}
